import { BaseModal } from '@/components/common';
import { formatDate } from '@/lib/utils';
import type { Resource } from '@/types/resources';
import { Badge, Typography } from 'antd';

const { Title, Text } = Typography;

interface ResourceViewModalProps {
  resource: Resource | null;
  open: boolean;
  onClose: () => void;
  getTypeColor: (type: string) => 'blue' | 'green' | 'orange' | 'purple' | 'pink' | 'cyan' | 'default';
}

export function ResourceViewModal({
  resource,
  open,
  onClose,
  getTypeColor,
}: ResourceViewModalProps) {
  if (!resource) return null;

  return (
    <BaseModal
      open={open}
      onCancel={onClose}
      onOk={onClose}
      title="Resource Details"
      footer={null}
      width={800}
    >
      <div className="space-y-4">
        <div>
          <Title level={4}>{resource.title}</Title>
          <Text type="secondary" className="block mt-1">{resource.description}</Text>
        </div>
        {resource.cover && (
          <div>
            <Text type="secondary" className="block mb-2">Cover Image</Text>
            <div className="mb-4">
              <img
                src={resource.cover}
                alt={resource.title}
                className="max-w-full h-auto rounded-lg border object-cover"
                style={{ maxHeight: '400px' }}
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            </div>
          </div>
        )}
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <Text type="secondary">Type</Text>
            <div className="capitalize">
              <Badge color={getTypeColor(resource.type)} text={resource.type} />
            </div>
          </div>
          <div>
            <Text type="secondary">Status</Text>
            <div>
              <Badge color={resource.is_active ? 'success' : 'default'} text={resource.is_active ? 'Active' : 'Inactive'} />
            </div>
          </div>
          <div>
            <Text type="secondary">Created</Text>
            <div>{formatDate(resource.created_at)}</div>
          </div>
        </div>
        {resource.url && (
          <div>
            <Text type="secondary">URL</Text>
            <div>
              <a 
                href={resource.url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline"
              >
                {resource.url}
              </a>
            </div>
          </div>
        )}
      </div>
    </BaseModal>
  );
}

